import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ImagePlus, Link as LinkIcon } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && !u.startsWith("http") ? `${BACKEND_URL}${u}` : u);

export default function PhotosTab({ packages }) {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/photos");
            setPhotos(data.map((p) => ({ ...p, url: abs(p.url) })));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const remove = async (id) => {
        try {
            await api.delete(`/admin/photos/${id}`);
            toast.success("Photo removed.");
            load();
        } catch {
            toast.error("Could not remove.");
        }
    };

    const upload = async (slot, file, alt = "") => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("slot", slot);
        fd.append("alt", alt);
        try {
            await api.post("/admin/photos", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Photo uploaded.");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed.");
        }
    };

    const importUrl = async (slot, url, alt = "") => {
        if (!url) return;
        try {
            await api.post("/admin/photos/import-url", { url, slot, alt });
            toast.success("Photo imported.");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed.");
        }
    };

    const byActiveSlot = (slot) => photos.find((p) => p.slot === slot);
    const galleryItems = photos.filter((p) => p.slot === "gallery");

    if (loading) {
        return (
            <div
                className="bg-white border border-palm-400/30 rounded-sm p-8 text-sm text-forest-700/60"
                data-testid="photos-loading"
            >
                Loading photo library…
            </div>
        );
    }

    return (
        <div data-testid="photos-tab" className="space-y-8">
            <UrlImportPanel
                packages={packages}
                onImport={(slot, url, alt) => importUrl(slot, url, alt)}
            />

            <div className="bg-white border border-palm-400/30 rounded-sm p-6">
                <h3 className="font-serif text-2xl text-forest-700 mb-1">
                    Page photos
                </h3>
                <p className="text-sm text-forest-700/60 mb-6">
                    Upload your own photos to replace the homepage hero and About
                    page portrait. Newest upload wins per slot.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SlotCard
                        slot="hero"
                        title="Home hero"
                        description="Full-width background on the homepage."
                        photo={byActiveSlot("hero")}
                        onUpload={(f) => upload("hero", f, "Coral Bay hero")}
                        onDelete={(id) => remove(id)}
                    />
                    <SlotCard
                        slot="about"
                        title="About portrait"
                        description="Tall portrait on the About page."
                        photo={byActiveSlot("about")}
                        onUpload={(f) => upload("about", f, "Erin Pryor Durrell")}
                        onDelete={(id) => remove(id)}
                    />
                </div>
            </div>

            {/* Package photos */}
            <div className="bg-white border border-palm-400/30 rounded-sm p-6">
                <h3 className="font-serif text-2xl text-forest-700 mb-1">
                    Menu package photos
                </h3>
                <p className="text-sm text-forest-700/60 mb-6">
                    Each menu package shows one signature photo. Upload your dish.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {packages.map((pkg) => (
                        <SlotCard
                            key={pkg.id}
                            slot={`package:${pkg.id}`}
                            title={pkg.name}
                            description={
                                pkg.category === "drop_off" ? "Drop-Off" : "On-Site Chef"
                            }
                            photo={byActiveSlot(`package:${pkg.id}`)}
                            onUpload={(f) => upload(`package:${pkg.id}`, f, pkg.name)}
                            onDelete={(id) => remove(id)}
                            compact
                        />
                    ))}
                </div>
            </div>

            {/* Gallery */}
            <div className="bg-white border border-palm-400/30 rounded-sm p-6">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-serif text-2xl text-forest-700">
                        Home gallery
                    </h3>
                    <GalleryUploader
                        onUpload={(f) => upload("gallery", f, "")}
                    />
                </div>
                <p className="text-sm text-forest-700/60 mb-6">
                    Add as many photos as you'd like. Shows in a grid on the
                    homepage.
                </p>
                {galleryItems.length === 0 ? (
                    <div className="border border-dashed border-palm-400/40 rounded-sm p-10 text-center text-sm text-forest-700/50">
                        No gallery photos yet. Upload your first one above.
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {galleryItems.map((g) => (
                            <div
                                key={g.id}
                                className="relative aspect-square overflow-hidden rounded-sm bg-sand-200 group"
                                data-testid={`gallery-item-${g.id}`}
                            >
                                <img
                                    src={g.url}
                                    alt={g.alt || ""}
                                    className="w-full h-full object-cover"
                                />
                                <button
                                    onClick={() => remove(g.id)}
                                    data-testid={`gallery-delete-${g.id}`}
                                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 text-forest-900 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 hover:text-white"
                                    aria-label="Remove photo"
                                >
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function SlotCard({ slot, title, description, photo, onUpload, onDelete, compact }) {
    const inputRef = useRef(null);
    return (
        <div
            className="border border-palm-400/30 rounded-sm overflow-hidden bg-sand-50"
            data-testid={`slot-${slot}`}
        >
            <div className={`relative ${compact ? "aspect-[4/3]" : "aspect-[16/10]"} bg-sand-200`}>
                {photo ? (
                    <img src={photo.url} alt={photo.alt || title} className="w-full h-full object-cover" />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-forest-700/40">
                        <ImagePlus className="w-10 h-10" strokeWidth={1.2} />
                    </div>
                )}
            </div>
            <div className="p-4">
                <div className="flex items-baseline justify-between">
                    <div className="font-serif text-lg text-forest-700">{title}</div>
                    <span className="text-xs text-forest-700/60">{description}</span>
                </div>
                <div className="mt-3 flex gap-2">
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid={`file-input-${slot}`}
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) onUpload(f);
                            e.target.value = "";
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        data-testid={`upload-btn-${slot}`}
                        className="inline-flex items-center gap-2 text-xs tracking-wide bg-forest-700 text-sand-100 px-3 py-2 rounded-sm hover:bg-forest-900"
                    >
                        <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {photo ? "Replace" : "Upload"}
                    </button>
                    {photo && (
                        <button
                            type="button"
                            onClick={() => onDelete(photo.id)}
                            data-testid={`delete-btn-${slot}`}
                            className="inline-flex items-center gap-2 text-xs tracking-wide text-forest-700/70 hover:text-red-700 px-2 py-2"
                        >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            Remove
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function GalleryUploader({ onUpload }) {
    const inputRef = useRef(null);
    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                data-testid="gallery-file-input"
                onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    files.forEach((f) => onUpload(f));
                    e.target.value = "";
                }}
            />
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid="gallery-upload-btn"
                className="inline-flex items-center gap-2 text-xs tracking-wide bg-forest-700 text-sand-100 px-3 py-2 rounded-sm hover:bg-forest-900"
            >
                <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                Add photos
            </button>
        </>
    );
}

function UrlImportPanel({ packages, onImport }) {
    const [url, setUrl] = useState("");
    const [slot, setSlot] = useState("gallery");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!url) return;
        setBusy(true);
        await onImport(slot, url);
        setBusy(false);
        setUrl("");
    };

    return (
        <div
            className="bg-white border border-palm-400/30 rounded-sm p-6"
            data-testid="url-import-panel"
        >
            <div className="flex items-center gap-2 mb-1">
                <LinkIcon className="w-4 h-4 text-forest-700" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl text-forest-700">
                    Import from URL
                </h3>
            </div>
            <p className="text-sm text-forest-700/60 mb-4">
                Paste any public image URL — from Instagram, Pinterest, iCloud
                shared link, or a shared photo. Tip: right-click your Instagram
                photo (desktop) or long-press → "Copy image address".
            </p>
            <form
                onSubmit={submit}
                className="grid grid-cols-1 md:grid-cols-[1fr_240px_auto] gap-3"
            >
                <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://…image.jpg"
                    required
                    data-testid="import-url-input"
                    className="bg-sand-50 border border-palm-400/50 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-forest-700"
                />
                <select
                    value={slot}
                    onChange={(e) => setSlot(e.target.value)}
                    data-testid="import-url-slot"
                    className="bg-sand-50 border border-palm-400/50 rounded-sm px-3 py-2.5 text-sm"
                >
                    <optgroup label="Page photos">
                        <option value="hero">Home hero</option>
                        <option value="about">About portrait</option>
                        <option value="gallery">Gallery</option>
                    </optgroup>
                    <optgroup label="Menu packages">
                        {packages.map((p) => (
                            <option key={p.id} value={`package:${p.id}`}>
                                {p.name}
                            </option>
                        ))}
                    </optgroup>
                </select>
                <button
                    type="submit"
                    disabled={busy || !url}
                    data-testid="import-url-btn"
                    className="inline-flex items-center gap-2 bg-forest-700 text-sand-100 px-5 py-2.5 rounded-sm hover:bg-forest-900 disabled:opacity-50 text-sm tracking-wide"
                >
                    {busy ? "Importing…" : "Import"}
                </button>
            </form>
            <p className="text-xs text-forest-700/50 mt-3 leading-relaxed">
                If Instagram blocks the fetch, the safest path is: open the post →
                right-click the image → <em>Copy image address</em> → paste here.
                Works with any image URL, not just Instagram.
            </p>
        </div>
    );
}
